const MAX_ITERATIONS = 20

const isNullish = value => value === null || typeof value === 'undefined'

const isBlank = value => {
  if (isNullish(value))
    return true
  if (Array.isArray(value))
    return value.length === 0
  return String(value).trim() === ''
}

const inMatch = (expected, actual) => {
  const values = String(expected ?? '').split(';').filter(Boolean)
  if (Array.isArray(actual))
    return actual.some(item => values.indexOf(String(item)) !== -1)
  return values.indexOf(String(actual)) !== -1
}

// The datetime_value arrives as ISO8601 (initial) or flatpickr canonical (edited); slice per format.
const canonicalDatetime = (raw, format) => {
  const value = String(raw ?? '').trim()
  if (!value)
    return null
  if (format === 'only_time')
    return value.length > 5 ? value.slice(11, 16) : value
  const date = value.slice(0, 10)
  if (format === 'only_date')
    return date
  const time = value.slice(11, 16)
  return time ? `${date} ${time}` : date
}

const isNumeric = value => String(value ?? '').trim() !== '' && Number.isFinite(Number(value))

// Numbers compare numerically only when both sides are numeric; otherwise lexical string order
// (canonical datetime/time strings included, since they sort chronologically as strings).
const asComparable = (expected, actual) => {
  if (isNumeric(expected) && isNumeric(actual))
    return [Number(expected), Number(actual)]
  return [String(expected), String(actual)]
}

// Server (evaluator.rb) only guards on nil, not on blank: an empty string still compares as ''.
const compareMatch = (compare, expected, actual) => {
  if (isNullish(actual))
    return false
  const [comparableExpected, comparableActual] = asComparable(expected, actual)
  return compare(comparableActual, comparableExpected)
}

const greaterThan = (actualValue, expectedValue) => actualValue > expectedValue
const lessThan = (actualValue, expectedValue) => actualValue < expectedValue
const greaterOrEqual = (actualValue, expectedValue) => actualValue >= expectedValue
const lessOrEqual = (actualValue, expectedValue) => actualValue <= expectedValue

// Mirrors Conditions::Evaluator#inc_match?: substring match on a scalar actual.
// Split-expected intersection when the actual is an array (selection_multi).
const incMatch = (expected, actual) => {
  if (Array.isArray(actual))
    return inMatch(expected, actual)
  return !isBlank(actual) && String(actual).indexOf(String(expected)) !== -1
}

const COMPARATORS = {
  eq: (expected, actual) => String(actual) === String(expected),
  neq: (expected, actual) => String(actual) !== String(expected),
  gt: (expected, actual) => compareMatch(greaterThan, expected, actual),
  lt: (expected, actual) => compareMatch(lessThan, expected, actual),
  gte: (expected, actual) => compareMatch(greaterOrEqual, expected, actual),
  lte: (expected, actual) => compareMatch(lessOrEqual, expected, actual),
  inc: incMatch,
  ninc: (expected, actual) => !incMatch(expected, actual),
  empty: (expected, actual) => isBlank(actual),
  filled: (expected, actual) => !isBlank(actual),
  in: (expected, actual) => inMatch(expected, actual),
  nin: (expected, actual) => !inMatch(expected, actual)
}

const evaluate = (state, expected, actual) => {
  const comparator = COMPARATORS[state]
  return comparator ? comparator(expected, actual) : false
}

// Unsaved pin edits live in mark_pins_attributes; mark_pins only holds what the payload delivered.
const currentPins = mark => {
  const pending = mark.mark_pins_attributes
  if (pending)
    return pending.filter(pin => !pin.markedForDestroy)
  return mark.mark_pins || []
}

const ATTACHED_VALUE = 'attached'

const attachedIf = value => value ? ATTACHED_VALUE : null

// A signature drawn or erased in the page outranks the saved one; undefined means untouched.
const signatureValue = mark => {
  const pending = mark.signatureData
  if (typeof pending === 'undefined')
    return attachedIf(mark.signature_url)
  return attachedIf(pending)
}

// A typed-in choice is not an option until the answer is saved, like Mark#selected_option_value.
const selectedOptionValue = mark => {
  if (!isNullish(mark.option_id))
    return String(mark.option_id)
  return mark.new_option_content ? `new:${mark.new_option_content}` : null
}

// Grade and selection expose the raw option id; observedValue maps it to a note.
// An unanswered column stays null here, matching Mark#conditional_value.
const rawMarkValue = mark => {
  const { context } = mark.question
  if (context === 'grade' || context === 'selection')
    return selectedOptionValue(mark)
  if (context === 'selection_multi')
    return (mark.option_ids || []).map(String)
  if (context === 'datetime')
    return canonicalDatetime(mark.datetime_value, mark.question.datetime_format)
  if (context === 'plan')
    return attachedIf(currentPins(mark).length)
  if (context === 'photos')
    return attachedIf((mark.photos || []).length)
  if (context === 'signature')
    return signatureValue(mark)
  return mark.value
}

const gradedValue = (gradeMap, live) => {
  if (!gradeMap)
    return live
  return isBlank(live) ? null : gradeMap[live]
}

// Payload values are already canonical (Mark#conditional_value), hence no grade mapping on them.
// A scope table is authoritative: values.questions crosses scopes, where the server sees no mark.
const fallbackValue = ({ payload, additional }, questionId) => {
  const scopedValues = isNullish(additional) ? null : payload.values.scopes?.[String(additional)]
  if (scopedValues)
    return scopedValues[questionId]
  return payload.values.questions[questionId]
}

const observedValue = (ctx, condition) => {
  const { payload, marksById, gradeOptions, hidden } = ctx
  if (condition.type === 'Variable')
    return payload.values.variables[condition.id]
  if (hidden[condition.id])
    return null

  const mark = marksById[condition.id]
  if (!mark)
    return fallbackValue(ctx, condition.id)
  return gradedValue(gradeOptions[condition.id], rawMarkValue(mark))
}

const ruleSatisfied = (ctx, rule) => {
  const results = rule.conditions.map(
    condition => evaluate(condition.state, condition.value, observedValue(ctx, condition))
  )
  return rule.operator === 'every' ? results.every(Boolean) : results.some(Boolean)
}

const vote = (kind, satisfied) => {
  if (!satisfied)
    return 'none'
  return kind === 'show' ? 'show' : 'hide'
}

// A 'hide' vote wins; a question targeted by show-actions stays hidden until one fires, else visible.
const visibleFrom = (votes, hasShow) => {
  if (votes.indexOf('hide') !== -1)
    return false
  if (hasShow)
    return votes.indexOf('show') !== -1
  return true
}

const buildMap = ctx => {
  const entries = {}
  ctx.payload.rules.forEach(rule => {
    const satisfied = ruleSatisfied(ctx, rule)
    rule.actions.forEach(action => {
      if (!entries[action.id])
        entries[action.id] = []
      entries[action.id].push({ kind: action.kind, satisfied })
    })
  })

  const map = {}
  Object.keys(entries).forEach(questionId => {
    const list = entries[questionId]
    const votes = list.map(entry => vote(entry.kind, entry.satisfied))
    const hasShow = list.some(entry => entry.kind === 'show')
    map[questionId] = visibleFrom(votes, hasShow)
  })
  return map
}

const sameKeys = (left, right) => {
  const leftKeys = Object.keys(left)
  if (leftKeys.length !== Object.keys(right).length)
    return false
  return leftKeys.every(key => Object.hasOwn(right, key))
}

export const marksByQuestionId = parallels => {
  const map = {}
  parallels.forEach(parallel => {
    parallel.marks.forEach(mark => { map[mark.question.id] = mark })
  })
  return map
}

// Fixpoint iteration: hiding a question turns its observed value blank, which can cascade.
export const computeHiddenQuestionIds = (payload, marksById, options = {}) => {
  if (!payload || !payload.rules || !payload.rules.length)
    return {}

  const gradeOptions = payload.grade_options || {}
  const { additional } = options
  let hidden = {}
  for (let iteration = 0; iteration < MAX_ITERATIONS; iteration += 1) {
    const map = buildMap({ payload, marksById, gradeOptions, hidden, additional })
    const next = {}
    Object.keys(map).forEach(questionId => {
      if (!map[questionId])
        next[questionId] = true
    })
    if (sameKeys(next, hidden))
      break
    hidden = next
  }
  return hidden
}

const scopeOf = parallel => Boolean(parallel.is_additional)

// Mirrors VisibilityResolver#base_parallels: same-scope parallels only, the parallel's own marks win.
export const hiddenByParallel = (payload, parallels) => {
  const maps = {}
  parallels.forEach(parallel => {
    const additional = scopeOf(parallel)
    const others = parallels.filter(other => other.id !== parallel.id && scopeOf(other) === additional)
    maps[parallel.id] = computeHiddenQuestionIds(
      payload, marksByQuestionId([...others, parallel]), { additional }
    )
  })
  return maps
}
